// @flow
import RouteNode from '../classes/RouteNode';
import Route from '../classes/Route';
import Position from '../classes/Position';
import Waypoint from '../classes/Waypoint';
import Connection from '../classes/Connection';
import TinyQueue from '../utilities/TinyQueue';

/**
 * An IIFE service which provides methods for loading and traversing Wayfinding graphs
 * 
 * @function RoutingService
 * @returns {object} Various public interface methods
 */
const RoutingService = (function() {
    let waypoints = {
        // HACK: Just hydrating a test waypoint
        "Level11.Floor20.GiantTV": new Waypoint(
                "Level11.Floor20.GiantTV",
                "Level11.Floor20.GiantTV",
                4950,
                204,
                0,
                "Level11.Floor20"
            )
    }, connections = {}, routeCache = {};

    // *** Public interface ***//

    return {
        InitializeRouting: function (loadedSVG) {
            waypoints = Object.assign(waypoints, createWaypointsFromNodes(loadedSVG.getElementsByTagName("point")));
            connections = Object.assign(connections, createConnectionsFromNodes(loadedSVG.getElementsByTagName("connector")));
        },
        GetRoute: function (origin, destination) {
            // TODO: Handle more complex route cache clearing scenarios (updated waypoint/connection data, time, etc.)
            if (routeCache[getRouteID(origin, destination)]) {
                return routeCache[getRouteID(origin, destination)];
            }
            return generateRoute(origin, destination);
        },
        GetWaypointNearestPosition: function (position, map) {
            return findNearestWaypoint(position, map);
        },
        GetWaypointByLocation: function(location) {
            return GetWaypointByLocation(location);
        }
    }

    // *** Private methods ***//

    /**
     * Creates an array of Waypoints from a given list of SVGElements
     * 
     * @param {Array<SVGElements>} waypointNodes An array of SVGElements
     * @returns {Array<Waypoint>} An array of Waypoints
     */
    function createWaypointsFromNodes (waypointNodes) {
        if (!waypointNodes) {
            throw new Error('RoutingService::createWaypointsFromNodes : Invalid waypoint nodes provided:' + waypointNodes);
        }

        let newWaypoints = {};

        for (var i = 0; i < waypointNodes.length; i++) {
            const waypointNode = waypointNodes[i];
            
            // Verify that node has data, is the correct type, and doesn't already exist
            if (!waypointNode.dataset || 
                !waypointNode.dataset.type ||
                waypointNode.dataset.type !== "waypoint" ||
                newWaypoints[waypointNode.id]) {
                continue;
            }

            // Create and store the waypoint object
            newWaypoints[waypointNode.id] = new Waypoint(
                waypointNode.id, 
                waypointNode.id,
                parseFloat( waypointNode.dataset.x),
                parseFloat( waypointNode.dataset.y),
                parseFloat( waypointNode.dataset.z)
            );
        }

        return newWaypoints;
    }

    /**
     * Creates an array of Connections from a given list of SVGElements
     * 
     * @param {Array<SVGElements>} connectionNodes An array of SVGElements
     * @returns {Array<Connection>} An array of Connections
     */
    function createConnectionsFromNodes (connectionNodes) {
        if (!connectionNodes) {
            throw new Error('RoutingService::createConnectionsFromNodes : Invalid connection nodes provided:' + connectionNodes);
        }

        let newConnections = {};

        for (var i = 0; i < connectionNodes.length; i++) {
            const connectionNode = connectionNodes[i];

            // Verify that node has data, is the correct type, doesn't already exist,
            // and that the connection is using known waypoints
            if (!connectionNode.dataset || 
                !connectionNode.dataset.type ||
                connectionNode.dataset.type !== "connection" ||
                newConnections[connectionNode.id] ||
                !waypoints[connectionNode.dataset.origin] ||
                !waypoints[connectionNode.dataset.destination] ) {
                continue;
            }

            // Create and store the connection object
            newConnections[connectionNode.id] = new Connection(
                connectionNode.dataset.origin,
                connectionNode.dataset.destination,
                connectionNode.dataset.walkability,
                (connectionNode.dataset.nonAda ? connectionNode.dataset.nonAda : false),
                connectionNode.dataset.bidirectional,
                connectionNode.dataset.stairs,
                connectionNode.dataset.crossFloor,
                waypoints[connectionNode.dataset.origin].position,
                waypoints[connectionNode.dataset.destination].position,
            );
        }

        return newConnections;
    }

    /**
     * Determines the connections coming from any given location
     * 
     * @param {string} location The label of a location
     * @returns {Array<Connection>} An array of connections
     */
    function getConnectionsByLocation (location) {
        let connectionsForLocation = [];
        
        Object.entries(connections).forEach(([key, val]) => {
            if (val.origin.toUpperCase() === location.toUpperCase() ||
                val.destination.toUpperCase() === location.toUpperCase()) {
                    connectionsForLocation.push(val);
                }
        });

        return connectionsForLocation;
    }

    /**
     * Determines the Route from any given origin and destination Waypoints
     * 
     * @param {Waypoint} originWaypoint The originating Waypoint of the Route
     * @param {Waypoint} destinationWaypoint The destination Waypoint of the Route
     * @returns {Route}
     */
    function generateRoute (originWaypoint, destinationWaypoint, ada = false) {
        if (originWaypoint === null || destinationWaypoint === null) {
            return null;
        }
        
        const originNode = new RouteNode(originWaypoint, null, 0.0);

        let closedSet = {};
        let openSet = new TinyQueue(null, costComparator);

        openSet.push(originNode);

        while (openSet.length !== 0) {
            const current = openSet.pop();

            const currentLocation = current.waypoint.id;

            closedSet[currentLocation] = true;

            if (currentLocation.toUpperCase() === destinationWaypoint.id.toUpperCase()) {
                const routeResult = new Route(current);
                routeCache[getRouteID(originWaypoint, destinationWaypoint)] = routeResult;
                return routeResult;
            }

            const connectionsCurrentNode = getConnectionsByLocation(currentLocation);
            if (connectionsCurrentNode === null || connectionsCurrentNode.length === 0) {
                continue;
            }

            for (var i = 0; i < connectionsCurrentNode.length; i++) {
                const connection = connectionsCurrentNode[i];
                let neighborLocation = null;

                // if calculating ada route and connection is non-ada, ignore and continue to next iteration
				if (ada && connection.nonAda) {
					continue;
				}
                
                if (connection.origin.toUpperCase() === currentLocation.toUpperCase()) {
                        neighborLocation = connection.destination;
                } else {
                        neighborLocation = connection.origin;
                }
                
                if (!waypoints[neighborLocation]) {
                    // if for some reason an orphaned connection exists, just ignore it when calculating route;
                    continue;
                }

                if (closedSet[neighborLocation]) {
                    continue;
                }

                const newNode = new RouteNode(GetWaypointByLocation(neighborLocation), current, connection.walkability);

                // Determine if node is in the open set
                let inOpenSet = false;
                const openSetArray = openSet.getData();
                for (var n = 0; n < openSetArray.length; n++) {
                    const node = openSetArray[n];
                    if (node.waypoint.id.toUpperCase() === neighborLocation.toUpperCase()) {
                        if(node.distanceToOrigin > newNode.distanceToOrigin) {
                            openSet.remove(node);
                            openSet.push(newNode);
                        }
                        inOpenSet = true;
                        break;        
                    }
                };

                if (!inOpenSet) {
                    openSet.push(newNode);
                }
            }
        }

        return null;

        /**
         * Comparison function for determining which of two RouteNodes is least expensive to get to the destination
         * 
         * @param {RouteNode} node1 
         * @param {RouteNode} node2 
         * @returns {number} 0, 1, or -1
         */
        function costComparator (node1: RouteNode, node2: RouteNode) {
            let totalCost1 = 0.0;
            let totalCost2 = 0.0;

            totalCost1 = node1.costToOrigin + Position.getDistance(node1.waypoint.position, destinationWaypoint.position);
            totalCost2 = node2.costToOrigin + Position.getDistance(node2.waypoint.position, destinationWaypoint.position);

            if (totalCost1 < totalCost2) {
                return -1;
            } else if (totalCost1 > totalCost2) {
                return 1;
            } else {
                return 0;
            }
        }
    }

    /**
     * Determines the nearest Waypoint to any given Position.  Optionally restrict to a given map.
     * 
     * @param {Position} position The position to find a nearest Waypoint
     * @param {string} map Optional map to restrict nearest waypoint
     * @returns {Waypoint} The nearest Waypoint
     */
    function findNearestWaypoint (position: Position, map = "") {
        if (!position) {
            throw new Error("RoutingService::findNearestWaypoint : Provided position was " + position + ".");
        }
        
        let minDist = -1, nearestWaypoint;
        Object.entries(waypoints)
            .filter(([key: string, val: Waypoint]) => key.indexOf(map) >= 0)
            .forEach(([key: string, val: Waypoint]) => {
                if (minDist === -1 ||
                    Position.getDistance(position, val.position) < minDist) {
                        nearestWaypoint = val;
                        minDist = Position.getDistance(position, val.position);
                    }
            });

        return nearestWaypoint;
    }

    /**
     * Returns a Waypoint by location string
     * 
     * @param {string} location Location string
     */
    function GetWaypointByLocation (location) {
        return waypoints[location];
    }

    /**
     * Handles creating a standard route ID based on parameters
     * // TODO: Add ADA and other route affecting parameters
     * 
     * @param {Waypoint} origin Origin waypoint of route
     * @param {Waypoint} destination Destination waypoint of route
     */
    function getRouteID (origin, destination) {
        return origin.location + "-" + destination.location;
    }
})();

export default RoutingService;


// WEBPACK FOOTER //
// ./src/services/Routing.js